import React from 'react';
import ReactDOM from 'react-dom/client';
import API, { GOOGLE_KEY } from './API';
import { createBrowserRouter, defer, Link, Outlet, RouterProvider } from 'react-router-dom';
import { BusinessProvider, PostingProvider, UserProvider } from './Hooks';
import { SidebarItem } from './Classes';
import { BusinessSidebarItem, PostingSidebarItem, SidebarBadgeItem } from './components';
import { Toast } from 'react-frontier';
import { APIProvider } from '@vis.gl/react-google-maps';

import 'fomantic-ui-css/semantic.css'
import 'frontier-css/css/frontier.css'
import './style/tempu.scss';

import SiteContainer from './SiteContainer';
import PostingList from './screens/PostingList';
import BusinessList from './screens/BusinessList';
import LoginResponse from './screens/LoginResponse';
import UserProfile from './screens/UserProfile';
import BusinessPostings from './screens/BusinessPostings';
import BusinessSettings from './screens/BusinessSettings';
import BusinessPayouts from './screens/BusinessPayouts';
import BusinessUsers from './screens/BusinessUsers';
import PostingDetails from './screens/posting/PostingDetails';
import PostingWorkers from './screens/posting/PostingWorkers';
import PostingPayout from './screens/posting/PostingPayout';
import PostingApplicants from './screens/posting/PostingApplicants';
import PostingPayoutInfo from './screens/posting/PostingPayoutInfo';
import Posting from './screens/Posting';
import UserPostings from './screens/UserPostings';
import Business from './screens/admin/Business';
import BusinessInfo from './screens/admin/BusinessInfo';
import Payouts from './screens/admin/Payouts';
import InvitationLink from './screens/posting/InvitationLink';
import UserPostingInvite from './screens/UserPostingInvite';
import PostingJobs from './screens/posting/PostingJobs';
import PostingJob from './screens/posting/PostingJob';
import AccountMoney from './screens/account/AccountMoney';
import AccountBanks from './screens/account/AccountBanks';
import Payout from './screens/admin/Payout';
import Users from './screens/admin/Users';
import UserInfo from './screens/admin/UserInfo';
import PendingUsers from './screens/admin/PendingUsers';
import PostingInvitations from './screens/posting/PostingInvitations';
import JobDetails from './screens/posting/JobDetails';

const PublicSidebar : SidebarItem[] = [
	{ text: 'Inicio', icon: 'home', path: '/' },
	{ text: 'Mi cuenta', icon: 'user', path: '/profile', login: true },
	{ text: 'Mi dinero', icon: 'money-bill-wave', path: '/profile/money', login: true },
	{ type: 'divider', login: true },
	{ text: 'Mis empleos', icon: 'clipboard', path: '/postings', login: true },
	{ text: 'Mis negocios', icon: 'building', path: '/business', login: true },
	{ text: 'Administración', icon: 'user', path: '/admin/business', admin: true },
]

var businessPath = (route: string)=>{
	return (url: string, p: { id: number })=>{
		return `/business/${p.id}${route}`
	}
}
const BusinessSidebar : SidebarItem[] = [
	{ type: 'element', key: 'sbr-bsn-item', path: businessPath('/settings'), element: (_: string, p: { id: number })=><BusinessSidebarItem id={p.id} /> },
	{ text: 'Empleos', icon: 'clipboard', path: businessPath('/postings'), active_paths: [businessPath('')] },
	{ text: 'Estado de cuenta', icon: 'money-bill-wave', path: businessPath('/payouts') },
	{ text: 'Usuarios', icon: 'user-cog', path: businessPath('/users') },
	{ text: 'Configuración', icon: 'cog', path: businessPath('/settings') },
]

var postingPath = (route: string)=>{
	return (url: string, p: { bid: number, pid: number })=>{
		return `/business/${p.bid}/posting/${p.pid}${route}`
	}
}
const PostingSidebar : SidebarItem[] = [
	{ type: 'element', key: 'sbr-pst-item', element: (_: string, p: { pid: number })=><PostingSidebarItem id={p.pid} /> },
	{ text: 'Información', icon: 'clipboard', path: postingPath('/') },
	{ text: 'Puestos', icon: 'users', path: postingPath('/jobs') },
	{ text: 'Empleados', icon: 'user-tie', path: postingPath('/workers') },
	{ text: 'Aplicantes', icon: 'user-plus', path: postingPath('/applicants'), },
	{ text: 'Liquidacion', icon: 'money-check-alt', path: postingPath('/payout') },
]

const AdminSidebar : SidebarItem[] = [
	{ text: 'Negocios', icon: 'building', path: '/admin/business' },
	{ text: 'Liquidaciones', icon: 'money-check-alt', path: '/admin/payouts' },
	{ text: 'Usuarios', icon: 'users', path: '/admin/users' },
	{ text: 'Pendientes', icon: 'users', path: '/admin/users/pendings' },
]

const AccountSidebar : SidebarItem[] = [
	{ text: 'Información', icon: 'user-edit', path: '/profile' },
	{ text: 'Mi dinero', icon: 'money-bill-wave', path: '/profile/money' },
	{ text: 'Cuentas bancarias', icon: 'credit-card', path: '/profile/bank' },
]

const Router = createBrowserRouter([{
	path: '/auth',
	element: <LoginResponse />
}, {
	// PUBLIC - No login required
	path: '/',
	errorElement: <SiteContainer items={PublicSidebar} error />,
	element: <SiteContainer items={PublicSidebar} outlet={<Outlet />} />,
	children: [
		{ path: '/', element: <PostingList /> },
		{ path: '/posting/:id', element: <Posting /> }
	]
}, {
	// RESTRICTED - Login required
	path: '/',
	errorElement: <SiteContainer items={PublicSidebar} error />,
	element: <SiteContainer items={PublicSidebar} login outlet={<Outlet />} />,
	children: [
		{ path: '/business', element: <BusinessList /> },
		{ path: '/postings', element: <UserPostings /> },
		{ path: '/posting/link/:token', element: <InvitationLink /> },
		{ path: '/posting/invite/:job_id', element: <UserPostingInvite /> },
		{ path: '/posting/job/:job_id', element: <JobDetails /> },
	]
}, {
	// ADMIN
	path: '/',
	errorElement: <SiteContainer items={AdminSidebar} returnUrl={'/'} error />,
	element: <SiteContainer items={AdminSidebar} returnUrl={'/'} login outlet={<Outlet />} />,
	children: [
		{ path: '/admin/business', element: <Business /> },
		{ path: '/admin/business/:business_id', element: <BusinessInfo /> },
		{ path: '/admin/payouts', element: <Payouts />  },
		{ path: '/admin/payouts/:payout_id', element: <Payout />  },
		{ path: '/admin/users', element: <Users />  },
		{ path: '/admin/users/:user_id', element: <UserInfo />  },
		{ path: '/admin/users/pendings', element: <PendingUsers />  },
	]
},{
	// BUSINESS
	path: '/',
	errorElement: <SiteContainer items={BusinessSidebar} returnUrl={'/business'} error />,
	element: <SiteContainer items={BusinessSidebar} returnUrl={'/business'} login outlet={<Outlet />} />,
	loader: async ({ params })=>{
		return defer({
			data: API.getBusinessAccess(parseInt(params.id)),
		});
	},
	children: [
		{ path: '/business/:id', element: <BusinessPostings /> },
		{ path: '/business/:id/payouts', element: <BusinessPayouts /> },
		{ path: '/business/:id/postings', element: <BusinessPostings /> },
		{ path: '/business/:id/users', element: <BusinessUsers /> },
		{ path: '/business/:id/users/:user_id', element: <BusinessUsers /> },
		{ path: '/business/:id/settings', element: <BusinessSettings /> },
	]
}, {
	// EDIT POSTING
	path: '/',
	errorElement: <SiteContainer items={PostingSidebar} returnUrl={(_, p)=>`/business/${p.bid}/postings`} error />,
	element: <SiteContainer items={PostingSidebar} returnUrl={(_, p)=>`/business/${p.bid}/postings`} login outlet={<Outlet />} />,
	loader: async ({ params })=>{
		return defer({
			data: API.getPostingAccess(parseInt(params.pid)),
		})
	},
	children: [
		{ path: '/business/:bid/posting/:pid', element: <PostingDetails />},
		{ path: '/business/:bid/posting/:pid/workers', element: <PostingWorkers /> },
		{ path: '/business/:bid/posting/:pid/invitations', element: <PostingInvitations /> },
		{ path: '/business/:bid/posting/:pid/applicants', element: <PostingApplicants /> , 
			loader: async ({ params }) => {
				var jobs = await API.getPostingJobs(parseInt(params.pid));
				return jobs.data;
			}
		},
		{ path: '/business/:bid/posting/:pid/payout', element: <PostingPayout /> },
		{ path: '/business/:bid/posting/:pid/payout/:pyid', element: <PostingPayoutInfo /> },
		{ path: '/business/:bid/posting/:pid/jobs', element: <PostingJobs /> },
		{ path: '/business/:bid/posting/:pid/jobs/:jid', element: <PostingJob /> }
	]
}, {
	// USER SETTINGS
	path: '/',
	errorElement: <SiteContainer returnUrl={'/'} items={AccountSidebar} error />,
	element: <SiteContainer returnUrl={'/'} items={AccountSidebar} login outlet={<Outlet />} />,
	children: [
		{ path: '/profile', element: <UserProfile self /> },
		{ path: '/profile/money', element: <AccountMoney /> },
		{ path: '/profile/bank', element: <AccountBanks /> }
	]
}])

const root = ReactDOM.createRoot(
	document.getElementById('root') as HTMLElement
);

root.render(
	<UserProvider>
		<BusinessProvider>
			<PostingProvider>
				<APIProvider apiKey={GOOGLE_KEY}>
					<Toast.Provider defaultOptions={{ duration: 8000 }}>
						<RouterProvider router={Router} />
					</Toast.Provider>
				</APIProvider>
			</PostingProvider>
		</BusinessProvider>
	</UserProvider>
);